import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Crown, Calendar, XCircle, Radio } from "lucide-react";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ViewerDashboard() {
  const { user } = useAuth();
  const { search } = useLocation();
  const nav = useNavigate();
  const [watchlist, setWatchlist] = useState([]);
  const [history, setHistory] = useState([]);
  const [memberships, setMemberships] = useState([]);

  // ?go-live=1 — handoff from /social/create LIVE tab and PlayerSidebar.
  // Creators are forwarded straight to the broadcaster console; viewers see
  // a friendly "apply to be a creator" CTA right at the top of the page.
  const goLive = new URLSearchParams(search).get("go-live") === "1";
  useEffect(() => {
    if (!goLive || !user) return;
    const role = user.role || "viewer";
    if (role === "creator" || role === "premium_creator" || role === "admin") {
      nav("/studio/live", { replace: true });
    }
  }, [goLive, user, nav]);

  useEffect(() => {
    api.get("/watchlist").then((r) => setWatchlist(r.data)).catch(() => {});
    api.get("/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/creator/subscriptions/mine").then((r) => setMemberships(r.data)).catch(() => {});
  }, []);

  const cancelMembership = async (s) => {
    if (!window.confirm(`Cancel auto-renew for ${s.creator_name}? You'll still have access until ${new Date(s.expires_at).toLocaleDateString()}.`)) return;
    try {
      await api.post(`/creator/subscriptions/${s.id}/cancel`);
      toast.success("Auto-renew cancelled");
      const r = await api.get("/creator/subscriptions/mine");
      setMemberships(r.data);
    } catch { toast.error("Could not cancel"); }
  };

  return (
    <div data-testid="viewer-dashboard" className="pt-32 pb-20 px-4 sm:px-8 md:px-12 max-w-7xl mx-auto">
      {/* GO LIVE handoff banner — viewers see a "become a creator" CTA;
          creators are auto-redirected to /studio/live by the effect above. */}
      {goLive && user && !["creator", "premium_creator", "admin"].includes(user.role || "viewer") && (
        <div data-testid="dashboard-go-live-banner" className="mb-8 rounded-2xl bg-gradient-to-r from-red-600/30 via-rose-600/20 to-amber-500/20 border border-red-500/40 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center shrink-0 animate-pulse">
            <Radio className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-xl text-white">Want to go live?</div>
            <div className="text-white/70 text-sm mt-1">Live broadcasting is a creator perk. Apply now and the studio is yours.</div>
          </div>
          <Link to="/apply-creator" data-testid="dashboard-apply-creator-btn" className="bg-white text-black font-black text-sm px-5 py-2.5 rounded-full hover:bg-white/90 inline-flex items-center gap-1.5">
            Apply as creator
          </Link>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-semibold">My Tap In TV</div>
          <h1 className="font-display font-black text-5xl text-white tracking-tighter mt-2">Welcome, {user?.name}</h1>
          <div className="text-white/60 mt-2">
            Plan: <span className="text-tv-brand font-semibold uppercase">{user?.subscription_tier?.replace("_", " ") || "Free"}</span>
          </div>
        </div>
        {user?.role !== "admin" && (
          <Link to="/subscribe" data-testid="dashboard-upgrade-btn" className="bg-tv-accent text-black font-bold px-5 py-2.5 rounded-full">Upgrade plan</Link>
        )}
      </div>

      <section className="mt-12">
        <h2 className="font-display font-bold text-2xl text-white mb-4">Continue Watching</h2>
        {history.length === 0 ? (
          <div className="text-white/60 text-sm">Start watching something — it'll show up here.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {history.map((v) => (
              <div key={v.id} className="relative">
                <VideoCard item={v} variant="wide" fullWidth />
                <div className="tv-progress mt-2">
                  <div style={{ width: `${Math.min(100, (v.progress_sec / (v.duration_sec || 1)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14">
        <h2 className="font-display font-bold text-2xl text-white mb-4">My Watchlist</h2>
        {watchlist.length === 0 ? (
          <div className="text-white/60 text-sm">Save content for later — it'll appear here.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {watchlist.map((v) => <VideoCard key={v.id} item={v} fullWidth />)}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="dashboard-memberships">
        <h2 className="font-display font-bold text-2xl text-white mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5 text-tv-accent" /> My Fan Club memberships
        </h2>
        {memberships.length === 0 ? (
          <div className="text-white/60 text-sm">No Fan Club memberships yet. Back your favorite creators from their channel page.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {memberships.map((s) => (
              <div key={s.id} data-testid={`dashboard-membership-${s.id}`} className="tv-glass rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between">
                  <Link to={`/creator/${s.creator_id}`} className="font-display font-bold text-white hover:text-tv-accent transition">
                    {s.creator_name}
                  </Link>
                  <span className="text-tv-accent font-black text-sm">${s.price_usd.toFixed(2)}<span className="text-white/40 text-xs font-normal">/mo</span></span>
                </div>
                <div className="text-white/70 text-xs mt-1">{s.tier_name}</div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-white/50">
                  <Calendar className="w-3 h-3" />
                  Renews {s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}
                  {s.auto_renew === false && <span className="text-amber-300 font-semibold">· auto-renew OFF</span>}
                </div>
                <div className="mt-3 flex gap-2">
                  <Link
                    to={`/creator/${s.creator_id}`}
                    className="text-xs bg-white/10 hover:bg-white/20 text-white font-semibold px-3 py-1.5 rounded-full"
                  >
                    Visit channel
                  </Link>
                  {s.auto_renew !== false && (
                    <button
                      data-testid={`dashboard-cancel-${s.id}`}
                      onClick={() => cancelMembership(s)}
                      className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                    >
                      <XCircle className="w-3 h-3" /> Cancel renew
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
